import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import { useRecommendedQueries } from "@memorang/features/src/qa-search/hooks/useRecommendedQueries";
import type { OriginalQuestionData } from "@memorang/features/src/qa-search/types/question";
import { EXCLUDED_EXAM_IDS } from "../components/QASearch/constants";
import { getUserPrompt } from "../components/QASearch/helpers";
import { resolveScoreContext } from "../helpers/answer-score";
import { buildItemCacheIdentity } from "../helpers/item-cache-identity";
import {
	buildOriginalQuestionData,
	getSelectionTexts,
	resolveChoiceExplanation,
} from "../helpers/original-question";
import { getParentStem, replaceBrackets } from "./useAIFeedback";
import useAIFeedbackStore from "./useAIFeedbackStore";
import type { PostAnswerState } from "./usePostAnswerState";
import { useQASearch } from "./useQASearch";
import useSessionStore from "./useSessionStore";

type PostAnswerStateSlice = Pick<
	PostAnswerState,
	| "currentAnswer"
	| "currentItem"
	| "currentUserResponse"
	| "isAiItemType"
	| "itemId"
	| "shouldRenderPostAnswer"
	| "topics"
	| "aiFeedback"
	| "aiResponse"
	| "aiTranscription"
	| "isAssessorItemType"
>;

type UsePostAnswerPromptParams = PostAnswerStateSlice & {
	examName: string;
	shouldShowAiDisclaimer: boolean;
	openAiDisclaimer: () => void;
	isStreaming: boolean;
	updateSelectedQuery: (query: string) => void;
	updateIsAssistantVisible: (visible: boolean) => void;
	isDesktopWeb: boolean;
};

const resolveAiUserResponse = (
	aiTranscription?: string,
	currentUserResponse?: unknown,
) =>
	aiTranscription ??
	(typeof currentUserResponse === "string" ? currentUserResponse : undefined);

export const usePostAnswerPrompt = ({
	aiFeedback,
	aiResponse,
	aiTranscription,
	currentAnswer,
	currentItem,
	currentUserResponse,
	examName,
	isAiItemType,
	isAssessorItemType,
	isDesktopWeb,
	isStreaming,
	itemId,
	openAiDisclaimer,
	shouldRenderPostAnswer,
	shouldShowAiDisclaimer,
	topics,
	updateIsAssistantVisible,
	updateSelectedQuery,
}: UsePostAnswerPromptParams) => {
	const router = useRouter();
	const { userLogicalId } = useAuthContext();
	const {
		currentExamContext: { organizationId, projectId },
	} = useExamContext();
	const { examId, contextId } = useQASearch();
	const appId = useAppStore((state) => state.app.id);
	const locale = i18n.language;

	const shouldRenderFollowUps =
		shouldRenderPostAnswer && !(isAiItemType && isStreaming);

	// Gated on shouldRenderPostAnswer: pre-answer these change per keystroke and
	// would churn the prompt-derived React Query key into request spam.
	const formStates = useSessionStore((state) =>
		shouldRenderPostAnswer ? state.formStates?.[itemId] : undefined,
	);
	const itemState = useSessionStore((state) =>
		shouldRenderPostAnswer ? state.itemStates?.get(itemId) : undefined,
	);
	const liveResponse = useAIFeedbackStore((state) =>
		shouldRenderPostAnswer ? state.userResponse?.[itemId] : undefined,
	);

	const { followUpPrompt, userPrompt, originalQuestionData } = useMemo(() => {
		const resolved = buildOriginalQuestionData({
			item: currentItem,
			answer: currentAnswer,
			itemState,
			formStates,
			liveResponse,
		});
		const explanation = isAiItemType
			? (aiFeedback ?? "")
			: resolveChoiceExplanation(currentItem);
		const score = resolveScoreContext({
			item: currentItem,
			answer: currentAnswer,
			aiResponse,
			isAssessorItemType,
		});
		return {
			// Follow-up generation stays item-scoped: no learner selection, score,
			// response, or AI feedback, so one cached result fits every learner.
			followUpPrompt: replaceBrackets(
				`${getParentStem(currentItem)}\n\n${getUserPrompt(
					{
						stem: resolved.stem,
						correctChoice: resolved.correctChoice,
						incorrectChoices: resolved.incorrectChoices,
						explanation: isAiItemType ? "" : explanation,
						topics,
					},
					examName,
				)}`,
			),
			userPrompt: replaceBrackets(
				`${getParentStem(currentItem)}\n\n${getUserPrompt(
					{
						stem: resolved.stem,
						correctChoice: resolved.correctChoice,
						incorrectChoices: resolved.incorrectChoices,
						selectedChoice: getSelectionTexts(resolved.selectedChoice),
						explanation,
						score,
						topics,
						userResponse: isAiItemType
							? resolveAiUserResponse(aiTranscription, currentUserResponse)
							: undefined,
					},
					examName,
				)}`,
			),
			originalQuestionData: {
				stem: resolved.stem,
				explanation,
				topics,
				choices: resolved.choices,
				selectedChoice: resolved.selectedChoice,
				correctChoice: resolved.correctChoice,
				incorrectChoices: resolved.incorrectChoices,
			} satisfies OriginalQuestionData,
		};
	}, [
		aiFeedback,
		aiResponse,
		aiTranscription,
		currentAnswer,
		currentItem,
		currentUserResponse,
		examName,
		formStates,
		isAiItemType,
		isAssessorItemType,
		itemState,
		liveResponse,
		topics,
	]);

	const cacheIdentity = useMemo(
		() =>
			buildItemCacheIdentity({
				organizationId,
				projectId,
				appId,
				examId,
				itemId,
				itemVersion: currentItem.versionId,
				locale,
			}),
		[
			appId,
			currentItem.versionId,
			examId,
			itemId,
			locale,
			organizationId,
			projectId,
		],
	);

	const queryEnabled =
		!EXCLUDED_EXAM_IDS.includes(examId) && Boolean(itemId && followUpPrompt);

	const shouldShowFollowUps = shouldRenderFollowUps && queryEnabled;

	const { recommendedQueries, isLoading: isRecommendedQueriesLoading } =
		useRecommendedQueries({
			userPrompt: followUpPrompt,
			vectorStoreParams: {
				examId,
				itemId: `${itemId}`,
				contextId,
			},
			userId: userLogicalId,
			loading: (isAiItemType && isStreaming) || !queryEnabled,
			outputLanguage: locale,
			appId,
			cacheIdentity,
		});

	const handleSelectFollowUp = useCallback(
		(query: string) => {
			if (shouldShowAiDisclaimer) {
				openAiDisclaimer();
				return;
			}
			updateSelectedQuery(query);
			if (isDesktopWeb) {
				updateIsAssistantVisible(true);
				return;
			}
			router.push({
				pathname: "/aila/[itemId]",
				params: {
					itemId,
					query,
				},
			});
		},
		[
			itemId,
			isDesktopWeb,
			openAiDisclaimer,
			router,
			shouldShowAiDisclaimer,
			updateIsAssistantVisible,
			updateSelectedQuery,
		],
	);

	return {
		cacheIdentity,
		handleSelectFollowUp,
		isRecommendedQueriesLoading,
		originalQuestionData,
		recommendedQueries,
		shouldShowFollowUps,
		userPrompt,
	};
};
